import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import CloudBand from '../components/common/CloudBand';
import s from './AuthCallbackPage.module.css';

/**
 * AuthCallbackPage — handles the Supabase email confirmation redirect.
 *
 * Supabase appends session tokens as a URL fragment after the user
 * clicks their confirmation link:
 *   /auth/callback#access_token=...&refresh_token=...&type=signup
 *
 * We extract the tokens, store them, then redirect to onboarding.
 * A 1.5-second delay gives the "You're all set!" design time to show.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { setSession } = useAuthStore();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const hash = window.location.hash.substring(1); // strip leading #
    const params = new URLSearchParams(hash);

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const type = params.get('type');

    if (!accessToken || !refreshToken) {
      // No tokens — send to login immediately (no delay for error path)
      navigate('/login', { replace: true });
      return;
    }

    // Store session in authStore, then redirect after a brief display delay
    setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(() => {
        // 1.5s delay so users see the "You're all set!" screen
        return new Promise((resolve) => setTimeout(resolve, 1500));
      })
      .then(() => {
        if (type === 'recovery') {
          navigate('/reset-password', { replace: true });
        } else {
          // Route through the app root so OnboardingGuard decides the next step
          // (the language gate fires here for users who haven't chosen one).
          navigate('/', { replace: true });
        }
      })
      .catch(() => {
        navigate('/login', { replace: true });
      });
  }, [navigate, setSession]);

  return (
    <main className={s.page} aria-live="polite" aria-busy="true">
      <img
        src="/assets/mascot-ling-email.webp"
        alt=""
        aria-hidden="true"
        className={s.mascot}
      />
      <CloudBand variant="cream" className={s.cloudBand} />
      <div className={s.creamFill} />
      <div className={s.content}>
        <p className={s.heading}>You&#8217;re all set!</p>
      </div>
    </main>
  );
}
